/**
 * Conversation List Item Component
 * Reusable component for displaying individual conversations in lists
 */

import React, { useState } from 'react';
import {
  ListItem,
  ListItemButton,
  IconButton,
  Tooltip,
  Box,
  Fade,
  Typography,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  DeleteOutlined as DeleteIcon,
  FileDownloadOutlined as DownloadIcon,
  MoreVert as MoreIcon,
  EditOutlined as EditIcon,
  ChatBubbleOutlineOutlined as ChatIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ConversationSummary } from '../../api/data-contracts';
import { formatDate } from '../../utils/formatting/formatters';

/**
 * The preview line is one line of plain text. The message it comes from is
 * Markdown, and shown raw it reads as "**Ja** - auch *kursiv*, `code`".
 */
const plainPreview = (markdown: string): string =>
  markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[\[?\d+(?:\.\d+)?\]?\]/g, '')
    .replace(/[*_~`]+/g, '')
    .replace(/^\s{0,3}(?:#{1,6}|>|[-+]|\d+\.)\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();

interface ConversationListItemProps {
  conversation: ConversationSummary;
  isSelected?: boolean;
  showActions?: boolean;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string, title: string) => void;
  /** Omit to hide the export action, e.g. where it makes no sense. */
  onExport?: (conversationId: string, anchor: HTMLElement) => void;
  onEdit: (conversation: ConversationSummary) => void;
  // Selection mode props
  selectionMode?: boolean;
  isSelectionChecked?: boolean;
  onToggleSelection?: (id: string) => void;
}

export const ConversationListItem: React.FC<ConversationListItemProps> = ({
  conversation,
  isSelected = false,
  showActions = true,
  onSelect,
  onDelete,
  onExport,
  onEdit,
  selectionMode = false,
  isSelectionChecked = false,
  onToggleSelection,
}) => {
  const { t, i18n } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  // One "more" button instead of a row of icons: two 44px buttons did not fit
  // the space the row keeps free and sat on top of the preview text, and
  // delete was one slip of the mouse away from export.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const preview = conversation.last_message_preview
    ? plainPreview(conversation.last_message_preview)
    : '';

  const handleSelect = () => {
    // In selection mode, toggle selection instead of navigating
    if (selectionMode && onToggleSelection) {
      onToggleSelection(conversation.id);
    } else {
      onSelect(conversation.id);
    }
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    // The row itself opens the conversation; without this a click on the
    // button would also navigate away.
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    // The open menu covers the list, so the row never hears the mouse leave
    // and would stay highlighted after the menu is gone.
    setIsHovered(false);
  };

  const handleExport = (e: React.MouseEvent) => {
    e.stopPropagation();
    // The format menu opens where this one was: on the "more" button.
    const anchor = menuAnchor;
    closeMenu();
    if (anchor) { onExport?.(conversation.id, anchor); }
  };

  const handleDelete = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    closeMenu();
    onDelete(conversation.id, conversation.title);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeMenu();
    onEdit(conversation);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (onToggleSelection) {
      onToggleSelection(conversation.id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect();
    } else if (e.key === 'Delete' && showActions && !selectionMode) {
      e.preventDefault();
      handleDelete(e);
    }
  };

  return (
    <ListItem
      disablePadding
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        mb: 1,
        borderRadius: 2,
        overflow: 'hidden',
      }}
      secondaryAction={
        // Only show delete icon in non-selection mode (checkbox is now on left)
        !selectionMode && showActions ? (
          <Fade in={isHovered || isSelected || Boolean(menuAnchor)} timeout={200}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={t('conversation.actions')} arrow>
                <IconButton
                  size="small"
                  onClick={openMenu}
                  aria-label={t('conversation.actions')}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(menuAnchor)}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                >
                  <MoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
                onClick={e => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 180 } } }}
              >
                <MenuItem onClick={handleEdit}>
                  <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('conversation.menu.rename')}</ListItemText>
                </MenuItem>
                {onExport ? (
                  <MenuItem onClick={handleExport}>
                    <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('conversation.menu.export')}</ListItemText>
                  </MenuItem>
                ) : null}
                <Divider />
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                  <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText>{t('conversation.menu.delete')}</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Fade>
        ) : undefined
      }
    >
      <ListItemButton
        onClick={handleSelect}
        selected={isSelected}
        onKeyDown={handleKeyDown}
        aria-label={`${t('conversation.select')} ${conversation.title}`}
        sx={{
          py: 1.5,
          px: 2,
          borderRadius: 2,
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'transparent',
          bgcolor: isSelected
            ? 'primary.lighter'
            : isSelectionChecked
              ? 'action.selected'
              : isHovered
                ? 'action.hover'
                : 'transparent',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            bgcolor: isSelected ? 'primary.lighter' : 'action.hover',
            borderColor: isSelected ? 'primary.main' : 'action.selected',
          },
          '&.Mui-focusVisible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
          // Make room for action buttons (only in non-selection mode)
          pr: !selectionMode && showActions ? 6 : 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%', minWidth: 0 }}>
          {/* Selection checkbox on left */}
          {selectionMode && (
            <Checkbox
              checked={isSelectionChecked}
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
              color="primary"
              size="small"
              aria-label={t('conversation.select')}
              sx={{
                mt: -0.5,
                ml: -1,
                transition: 'all 0.2s ease-in-out',
              }}
            />
          )}

          {/* Chat icon indicator */}
          <Box
            sx={{
              mt: 0.5,
              color: isSelected ? 'primary.main' : 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              transition: 'color 0.2s ease-in-out',
            }}
          >
            <ChatIcon fontSize="small" />
          </Box>

          {/* Text content */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Tooltip title={conversation.title} arrow placement="top" enterDelay={500}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected ? 'primary.main' : 'text.primary',
                  mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.2s ease-in-out',
                }}
              >
                {conversation.title}
              </Typography>
            </Tooltip>

            {preview && (
              <Tooltip title={preview} arrow placement="bottom" enterDelay={500}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mb: 0.5,
                  }}
                >
                  {preview}
                </Typography>
              </Tooltip>
            )}

            {/* Metadata row */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled',
                  fontSize: '0.7rem',
                }}
              >
                {t('conversation.messages', { count: conversation.message_count || 0 })}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled',
                  fontSize: '0.7rem',
                }}
              >
                •
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled',
                  fontSize: '0.7rem',
                }}
              >
                {formatDate(conversation.updated_at, i18n.language)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </ListItemButton>
    </ListItem>
  );
};